// @Version: 1.0.8
import { ChangeDetectorRef, EventEmitter, Output, Component, Input, OnDestroy, OnInit, forwardRef, signal, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validators } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { CustomValidators } from './custom-validators';
import { FieldItemRatingService } from './rating.service';
import { FC_RATING, ICON } from './rating.model';
import { TRANSLATE_PATH as TRANSLATE_PATH_SHARED } from 'src/app/shared/shared.model';
import { IFieldItem } from '../field-item.model';
import { ErrorMessagesService } from 'src/app/shared/services/errorMessages/error-messages.service';
import { Subscription } from 'rxjs';

@UntilDestroy()
@Component({
   selector: 'app-field-item-rating',
   templateUrl: './rating.component.html',
   providers: [
      {
         provide: NG_VALUE_ACCESSOR,
         useExisting: forwardRef(() => FieldItemRatingComponent),
         multi: true
      },
      {
         provide: NG_VALIDATORS,
         useExisting: forwardRef(() => FieldItemRatingComponent),
         multi: true
      },
      FieldItemRatingService,
      ErrorMessagesService
   ],
   standalone: false
})

export class FieldItemRatingComponent implements OnDestroy, ControlValueAccessor, Validators, OnChanges {

   public FC_RATING: typeof FC_RATING = FC_RATING; 
   public ICON: typeof ICON = ICON;
   public TRANSLATE_PATH_SHARED: typeof TRANSLATE_PATH_SHARED = TRANSLATE_PATH_SHARED; 

   public done$$ = signal(false);

   public fgRating: FormGroup = this.fb.group({});
   
   private fieldItem_: IFieldItem = {} as IFieldItem;
   private sub_?: Subscription;
   private currentItemHover_!: number;
   private isHovered_: boolean = false;


   @Input() clearValueIfDisabled: boolean = true;

   @Input()
   get fieldItem (): IFieldItem {
      return this.fieldItem_;
   }
   set fieldItem (value: IFieldItem) {
      this.fieldItem_ = value || {};
   }

   @Output() isRequiredMarker: EventEmitter<boolean> = new EventEmitter<boolean>();

   get currentItemHover (): number {
      return this.currentItemHover_;
   }

   set isHovered (value: boolean) {
      this.isHovered_ = value;
   }
   get isHovered (): boolean {
      return this.isHovered_;
   }

   set value (val: string) {  
      this.fgRating.get(FC_RATING.RATINGBUTTON)?.setValue(val);
   }
   get value (): string {
      return this.fgRating.get(FC_RATING.RATINGBUTTON)?.value ?? '';
   }


   constructor (private fb: FormBuilder, public sFieldItemRating: FieldItemRatingService, private vRating: CustomValidators, private cdRef: ChangeDetectorRef) {
   }

   ngOnChanges(changes: SimpleChanges): void {
      if (changes && changes.fieldItem &&  (JSON.stringify(changes.fieldItem.currentValue) != JSON.stringify(changes.fieldItem.previousValue))) {
         this.init(changes.fieldItem.currentValue);
      }
   }

   ngOnDestroy (): void {
      this.reset();
   }

   validate (control: AbstractControl): ValidationErrors | null {
      this.fgRating.updateValueAndValidity({emitEvent: true, onlySelf: true});
      this.fgRating.valid ? null : this.fgRating.get(FC_RATING.RATINGBUTTON)?.setErrors({required: true});
      return this.fgRating.valid ? null : this.fgRating.errors;
   }

   writeValue (value: number | string | undefined | null): void {
      if (value == undefined) return;
      if (typeof value == 'string' && (value.trim().length == 0 || !Number.isNaN(value))) value = undefined;
      this.fgRating.get(FC_RATING.RATINGBUTTON)?.setValue(value);
      
      if (typeof value == 'number') this.setDirtyTouched;
      this.onValidationChange();
   }
   private setDirtyTouched (): void {
      this.fgRating.get(FC_RATING.RATINGBUTTON)?.markAsDirty(); 
      this.fgRating.get(FC_RATING.RATINGBUTTON)?.markAsTouched();
   }

   registerOnChange (fn: any): void {
      this.onChange = fn
   }
   registerOnTouched (fn: any): void {
      this.onTouched = fn
   }

   private onTouched () { }
   private onChange (_: string) { }

   setDisabledState? (isDisabled: boolean): void {
      isDisabled ? this.setDisabledControls() : this.fgRating.enable();
      this.onValidationChange();
   }

   onValidationChange: any = () => {}; 

   registerOnValidatorChange? (fn: () => void): void {
      this.onValidationChange = fn;
   }


   private reset (): void {
      this.done$$.set(false);
      this.fgRating = this.fb.group({});
      this.fieldItem_ = {} as IFieldItem;
      this.currentItemHover_ = -1;
      this.isHovered_ = false;
      this.sFieldItemRating.reset();
   }

   private setDisabledControls (): void {
      this.fgRating.disable();
      if (this.clearValueIfDisabled) this.fgRating.get(FC_RATING.RATINGBUTTON)?.setValue("");
      this.fgRating.markAsDirty();
   }

   private init (fieldItem: IFieldItem): void {
      this.transformAttribute();
      this.initFormGroup(fieldItem)
      this.done$$.set(true);
   }

   /**
   * Attribut1 = List of ratingButtons
   * @returns 
   */
   private transformAttribute (): boolean {
      if (!this.fieldItem.attribute_1 || !this.fieldItem.attribute_2 || !this.fieldItem.attribute_3 || !this.fieldItem.attribute_4 || !this.fieldItem.attribute_5) return true;

      this.sFieldItemRating.maxNumber = +this.fieldItem.attribute_3;
      this.sFieldItemRating.minNumber = +this.fieldItem.attribute_5;

      if (this.fieldItem.required) this.sFieldItemRating.required = this.fieldItem.required;

      this.sFieldItemRating.createErrorMessages(this.fieldItem.attribute_6 || '');
      return this.sFieldItemRating.createRatingButtons(this.fieldItem.attribute_4 || '');
   }

   private initFormGroup (fieldItem: IFieldItem): boolean {
      this.fgRating =  this.fb.group({
         [FC_RATING.RATINGBUTTON]: this.fb.control({value: fieldItem.content ?? null, disabled: false}, {updateOn: 'change'})
      });
      if (this.fieldItem.required) this.fgRating.addValidators(this.vRating.isRequired(this.fgRating));

      if (this.sub_) {
         this.sub_.unsubscribe();
         this.sub_ = undefined;
      }

      this.sub_ = this.fgRating.get(FC_RATING.RATINGBUTTON)?.valueChanges.subscribe(value => {
         if (!this.value && this.fgRating.get(FC_RATING.RATINGBUTTON)?.pristine) return;
         this.onChange(this.value);
         this.onTouched();
      });

      this.isRequiredMarker.emit(fieldItem.required);
      return true;
   }

   public hoverEvent (index: number): void {
      this.isHovered_ = true;
      this.currentItemHover_ = index + +(this.fieldItem.attribute_5 ?? 0);
   }

   public formatLabel (value: number): string {
      return value.toLocaleString();
   }

   public getMax (): number {
      return Number(this.fieldItem.attribute_5 ?? 0) + Number(this.fieldItem.attribute_3 ?? 10) - 1;
   }

   public onKeyboardSpace (index: number) {
      this.fgRating.get(FC_RATING.RATINGBUTTON)?.markAsDirty();
      this.fgRating.get(FC_RATING.RATINGBUTTON)?.markAsTouched();
      this.fgRating.get(FC_RATING.RATINGBUTTON)?.setValue(index + +(this.fieldItem.attribute_5 ?? 0));   
      
      this.hoverEvent(index); 
   }
}